/**
 * What happens after credentials are accepted.
 *
 * Two paths arrive here with the same thing in hand — a bare 200 from /login,
 * and a 200 from /login/mfa once a challenge is answered — and both need the
 * identical follow-up: store the session, ask /api/user who this is, and let
 * them in only if they may use the web app at all. Keeping that in one place
 * is what stops the MFA path drifting away from the direct one.
 *
 * This deliberately does not navigate. The caller owns the router, and both
 * call sites do the same thing on success anyway, so passing `navigate` in
 * would buy nothing and would make the helper untestable without one.
 *
 * It also does not throw. Both failures here are "render this sentence", not
 * exceptional — and a throw would be indistinguishable at the call site from
 * the genuine network catch, which is precisely the confusion that let
 * Login.js:59 report every wrong password as "Network error".
 */

import { API_BASE_URL } from './api';
import { SESSION_STORAGE_KEYS } from './sessionExpiry';
import { readBody } from './httpResponse';

// This client is admin-only by design: a user without it is sent to the mobile
// app rather than given a session here.
const WEB_APP_PERMISSION = 'can_view_admin_dash';

// Kept verbatim from the strings Login.js has always rendered, so existing
// assertions keep meaning what they meant.
const ACCESS_DENIED_MESSAGE =
  "Access denied: You don't have permission to access the web application at this time. Please use the FiStone mobile app instead.";
const VERIFICATION_FAILED_MESSAGE = 'Unable to verify permissions. Please try again later.';

/**
 * Discard the session.
 *
 * Uses the shared key list rather than the four keys Login.js used to remove
 * by hand: that left `userPermissions` behind, so a denied login could leave a
 * stale permissions array for ProtectedRoute to read.
 */
const discardSession = () => {
  SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const store = (session) => {
  localStorage.setItem('accessToken', session.accessToken);
  localStorage.setItem('refreshToken', session.refreshToken);
  localStorage.setItem('userId', session.userId);
  localStorage.setItem('username', session.username);
};

/**
 * Store a freshly issued session and decide whether it may be used here.
 *
 * @param {{accessToken: string, refreshToken: string, userId: *, username: string}} session
 * @returns {Promise<{ok: true} | {ok: false, message: string}>}
 */
export const completeLogin = async (session) => {
  // Guard so the never-throws contract holds in fact and not merely by
  // convention — reading .accessToken off a missing session would throw.
  if (!session || !session.accessToken) {
    return { ok: false, message: VERIFICATION_FAILED_MESSAGE };
  }

  store(session);

  let body;
  try {
    const response = await fetch(`${API_BASE_URL}/user`, {
      headers: {
        // The token that was just issued, not the one in localStorage. Reading
        // it back would make this depend on store() having run first, which is
        // an ordering nobody would think to preserve.
        Authorization: `Bearer ${session.accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      discardSession();
      return { ok: false, message: VERIFICATION_FAILED_MESSAGE };
    }

    body = await readBody(response);
  } catch {
    // The request itself failed. Without this the rejection would escape to
    // the caller's catch and be reported as a network error, which is true but
    // less useful than saying which step could not be completed.
    discardSession();
    return { ok: false, message: VERIFICATION_FAILED_MESSAGE };
  }

  // A 200 whose body will not parse means we could not check, which is a
  // different thing from having checked and found the permission missing.
  if (!body.json) {
    discardSession();
    return { ok: false, message: VERIFICATION_FAILED_MESSAGE };
  }

  const { permissions } = body.json;
  if (!Array.isArray(permissions) || !permissions.includes(WEB_APP_PERMISSION)) {
    discardSession();
    return { ok: false, message: ACCESS_DENIED_MESSAGE };
  }

  localStorage.setItem('userPermissions', JSON.stringify(permissions));
  return { ok: true };
};
